import { useCallback, useEffect, useState } from 'react';
import { ArrowCircleUp } from '@phosphor-icons/react';
import { users as usersApi } from '../../api/endpoints';
import { maCoquille } from '../../native/maj';
import { useSession } from '../../store/SessionContext';
import { t } from '../../i18n';

/**
 * « Une nouvelle version t'attend sur l'App Store », ou sur le Play Store.
 *
 * L'interface se remplace toute seule, mais pas la coquille : les appels,
 * CallKit, les notifications, les images video sont du Swift, et une
 * version qui les change ne peut arriver que par l'App Store. Or rien ne
 * le disait — il fallait aller voir soi-meme, ou ne jamais savoir.
 *
 * Sur Android c'est pire : rien ne s'y remplace tout seul, l'interface
 * voyage dans le paquet. Chaque nouveaute n'y arrive que par le Play
 * Store, d'ou la meme fenetre, depuis le build 22.
 *
 * Ce que la base publie, c'est le dernier build **envoye a Apple et
 * approuve**, pas celui qu'on vient d'archiver : annoncer une version que
 * personne ne peut encore telecharger ferait passer l'application pour
 * cassee. `tools/annoncer-la-version.mjs` l'ecrit, le jour ou elle sort.
 *
 * Une fenetre qui attend une reponse, et une seule fois par version.
 *
 * Elle s'ouvre au premier passage dans l'application apres l'annonce, et
 * reste tant que « J'ai compris » n'a pas ete touche : quitter
 * l'application sans repondre ne vaut pas avoir lu, et le message revient
 * a l'ouverture suivante. Une fois touche, plus jamais pour ce numero, que
 * la mise a jour ait ete faite ou non — on previent, on ne harcele pas.
 * Seule une version plus recente rouvre la question.
 *
 * Le premier passage compte aussi le retour au premier plan, comme pour
 * les avertissements : une application de telephone vit des jours sans
 * etre relancee, et l'annonce attendrait sinon qu'iOS l'ait fermee.
 */

/**
 * A qui l'annonce s'adresse.
 *
 * `false` : ceux dont la coquille est en retard, et eux seuls. Une
 * coquille qui ne sait pas dire son numero en fait partie — voir plus
 * bas. `true` la montre a tout le monde, y compris a qui a deja la
 * version ; le texte le supporte, il dit « si tu ne l'as pas encore ».
 */
const POUR_TOUT_LE_MONDE = false;

/* Le nom date de la regle d'avant : un telephone qui a deja repondu garde
   sa marque, et ne reverra pas l'annonce de ce numero. */
const DEJA_COMPRISE = 'sirocco.maj.ecartee';

/**
 * La fiche de Sirocco dans le magasin du telephone, ouverte dans le
 * magasin lui-meme.
 *
 * `itms-apps` mene a l'App Store sans passer par Safari, `market` au Play
 * Store sans passer par Chrome. Rien de natif a ajouter pour cela : une
 * adresse qui sort de l'application est confiee au systeme par la
 * coquille — c'est deja ce qui fait marcher `mailto:` et `tel:`.
 * `NATIF_MINIMUM` n'a donc pas a bouger, et une coquille ancienne sait le
 * faire aussi.
 *
 * Dire « ouvre le magasin et cherche Sirocco » etait un geste de trop au
 * moment precis ou l'on venait d'obtenir un oui.
 */
const FICHES: Record<'ios' | 'android', string> = {
  ios: 'itms-apps://apps.apple.com/app/id6802330359',
  android: 'market://details?id=app.sirocco',
};

function ouvrirLeMagasin(plateforme: 'ios' | 'android'): void {
  window.location.href = FICHES[plateforme];
}

/**
 * La marque de la reponse, sur le telephone.
 *
 * Un stockage refuse fait revenir le message a chaque passage : entre
 * insister et se taire, c'est insister qui sert le membre.
 */
function dejaComprise(build: number): boolean {
  try {
    return localStorage.getItem(`${DEJA_COMPRISE}.${build}`) !== null;
  } catch {
    return false;
  }
}

function retenirLaReponse(build: number): void {
  try {
    localStorage.setItem(`${DEJA_COMPRISE}.${build}`, String(Date.now()));
  } catch {
    // Rien a retenir : le message reviendra, et c'est le moindre mal.
  }
}

export function MiseAJourDispo() {
  const { user } = useSession();
  const [dire, setDire] = useState<{
    version: string;
    build: number;
    forcee: boolean;
    plateforme: 'ios' | 'android';
  } | null>(null);

  const regarder = useCallback(() => {
    if (!user) return;
    /* En partant en arriere-plan, il n'y a personne a qui parler. */
    if (document.visibilityState === 'hidden') return;

    void (async () => {
      const coquille = await maCoquille();
      /* Sur le web il n'y a pas de coquille : rien a annoncer. */
      if (!coquille) return;
      const annonce = await usersApi.annonce(coquille.plateforme);
      if (!annonce) return;
      /*
        Une coquille qui ne sait pas dire son numero vaut zero, et c'est
        une reponse : le champ est arrive avec la 1.7 (15), donc elle lui
        est anterieure par construction. Se taire devant elle privait du
        message exactement ceux qu'il vise — les 1.5 et 1.6, qui recoivent
        l'interface mais pas le Swift. On ne compare donc que ce qui est
        connu.
      */
      const numero = coquille.numero;
      /* Une demande posee sous le compte passe devant tout : elle sert a
         relire l'annonce une fois, meme deja a jour, meme deja repondu. */
      /*
        Rien a dire, et la fenetre se referme si elle etait ouverte.

        Elle ne se retirait jamais d'elle-meme : ouverte une fois, elle
        attendait un bouton, meme revenu de l'App Store avec la version
        installee. Chaque retour au premier plan la rejuge donc.
      */
      if (!annonce.forcee) {
        if (!POUR_TOUT_LE_MONDE && numero > 0 && annonce.build <= numero) return setDire(null);
        /* Deja repondu pour ce numero : plus jamais, mise a jour faite ou
           non. La marque porte le numero, donc une version plus recente
           reposera la question. */
        if (dejaComprise(annonce.build)) return setDire(null);
      }
      setDire({ ...annonce, plateforme: coquille.plateforme });
    })().catch(() => undefined);
  }, [user]);

  useEffect(() => {
    regarder();
    document.addEventListener('visibilitychange', regarder);
    return () => document.removeEventListener('visibilitychange', regarder);
  }, [regarder]);

  if (!dire) return null;

  return (
    <div className="confirm-root">
      <div className="confirm__scrim" />
      <div className="confirm confirm--avertissement" role="dialog" aria-modal="true">
        <span className="confirm__marque confirm__marque--douce" aria-hidden="true">
          <ArrowCircleUp size={26} weight="fill" />
        </span>
        <p className="confirm__titre">
          {t.legal.miseAJour.disponible(dire.version)}
        </p>
        <p className="confirm__texte">
          {t.legal.miseAJour.texte}
        </p>
        <div className="confirm__actions">
          {/*
            Repousser reste a gauche, et c'est ce geste-la qui referme
            l'annonce pour de bon. Mettre a jour ne la referme pas : on
            peut renoncer une fois arrive sur l'App Store, et l'annonce
            doit alors revenir.
          */}
          <button
            className="confirm__no"
            onClick={() => {
              retenirLaReponse(dire.build);
              // La demande ne valait que pour cette fois.
              if (dire.forcee) void usersApi.annonceVue().catch(() => undefined);
              setDire(null);
            }}
          >
            {t.legal.miseAJour.plusTard}
          </button>
          <button
            className="confirm__yes"
            onClick={() => {
              /*
                Une annonce montree sur demande est lue des qu'on y repond,
                par l'un ou l'autre bouton. Seul « Plus tard » l'eteignait :
                revenu de l'App Store a jour, on la retrouvait encore, la
                demande passant par-dessus le numero de version. Le 26
                septembre 2026, sur le telephone d'Idris.
              */
              if (dire.forcee) void usersApi.annonceVue().catch(() => undefined);
              ouvrirLeMagasin(dire.plateforme);
            }}
          >
            {t.legal.miseAJour.mettreAJour}
          </button>
        </div>
      </div>
    </div>
  );
}
